import { useCallback, useEffect, useState } from "react"
import { usePostHog } from "posthog-js/react"
import { useEventListener } from "usehooks-ts"
import { zaraz } from "zaraz-ts"

import sanityClient from "#/providers/SanityStudioProvider"
import {
  resetDepositActionState,
  selectDepositActionStatus,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  ENABLE_DEFAULT_ANALYTICS_CONSENT,
  MOBILE_BREAKPOINT,
} from "#/shared/constants"

export const useMobileScreen = (): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(
    window.innerWidth < MOBILE_BREAKPOINT,
  )

  const onResize = useCallback(() => {
    const isNowMobile = window.innerWidth < MOBILE_BREAKPOINT
    setIsMobile(isNowMobile)
  }, [])

  useEventListener("resize", onResize)

  useEffect(() => {
    onResize()
  }, [onResize])

  return isMobile
}

export const useBoostModal = (onClose: () => void) => {
  const [showLeaveModal, setShowLeaveModal] = useState(false)

  const depositActionStatus = useDappSelector(selectDepositActionStatus)
  const dispatch = useDappDispatch()

  const modalCloseHandler = useCallback(() => {
    if (depositActionStatus === "completed") {
      dispatch(resetDepositActionState())
      onClose()
    } else {
      setShowLeaveModal(true)
    }
  }, [onClose, depositActionStatus, dispatch])

  const leaveModalHandler = useCallback(() => {
    if (showLeaveModal) {
      dispatch(resetDepositActionState())
      onClose()
      setShowLeaveModal(false)
    } else {
      setShowLeaveModal(true)
    }
  }, [dispatch, onClose, showLeaveModal])

  const closeCompletedModalHandler = useCallback(() => {
    onClose()
    setShowLeaveModal(false)
    dispatch(resetDepositActionState())
  }, [onClose, dispatch])

  return {
    showLeaveModal,
    closeModal: modalCloseHandler,
    leaveModal: leaveModalHandler,
    closeCompletedModal: closeCompletedModalHandler,
    backToModal: () => setShowLeaveModal(false),
  }
}

export const useWithdrawalModal = (onClose: () => void) => {
  const [showLeaveModal, setShowLeaveModal] = useState(false)
  const dispatch = useDappDispatch()

  const leaveWithdrawalModalHandler = useCallback(() => {
    if (showLeaveModal) {
      onClose()
      setShowLeaveModal(false)
      dispatch(resetDepositActionState())
    } else {
      setShowLeaveModal(true)
    }
  }, [onClose, showLeaveModal, dispatch])

  return {
    showLeaveModal,
    leaveWithdrawalModalHandler,
    backToWithdrawalModal: () => setShowLeaveModal(false),
  }
}

export enum SanityCmsDataType {
  Learn = "learn",
  Landing = "landing",
  Explore = "explore",
  BorrowApp = "borrowApp",
  Borrow = "borrow",
  Musd = "musd",
  BorrowTestnet = "borrowAppTestnet",
  ExploreTestnet = "exploreTestnet",
  LearnTestnet = "learnTestnet",
  Growth = "growth",
  Faq = "faq",
  Header = "header",
  Downtime = "downtime",
}

export const useSanityCmsData = <T>(type: SanityCmsDataType) => {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedData = await sanityClient.fetch<T>(
          `*[_type == "${type}"][0]`,
        )

        if (!fetchedData) {
          throw new Error("Data not found")
        }
        setData(fetchedData)
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred",
        )
      }
    }

    fetchData()
  }, [type])

  return { data, error }
}

export const useZarazAnalyticsConsent = () => {
  const POSTHOG_ANALYTICS_CONSENT_ID = "eQic"

  const [hasConsent, setHasConsent] = useState(false)
  const posthog = usePostHog()

  useEffect(() => {
    const getConsent = async () => {
      const consent = zaraz.consent.get(POSTHOG_ANALYTICS_CONSENT_ID)
      const constentGranted = !!consent

      setHasConsent(constentGranted)

      if (constentGranted) {
        const defaultUserID = posthog.get_distinct_id()
        posthog.identify(defaultUserID)
      }
    }

    document.addEventListener("zarazConsentChoicesUpdated", getConsent)

    if (!("zaraz" in window)) {
      // Zaraz is not loaded, so we can't check for consent. On environments
      // where Zaraz is not loaded, for example internal testing environments,
      // we use the flag to enable analytics by default.
      setHasConsent(!!ENABLE_DEFAULT_ANALYTICS_CONSENT)
      // eslint-disable-next-line no-console -- For debugging purposes
      console.log(
        `📈 Zaraz not loaded, assuming analytics consent: ${ENABLE_DEFAULT_ANALYTICS_CONSENT}`,
      )
      return () => {}
    }

    return () => {
      document.removeEventListener("zarazConsentChoicesUpdated", getConsent)
    }
  }, [posthog])

  return { hasConsent }
}
